import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";

const CreateTicket = ({ onTicketCreated }) => {
  const { t } = useTranslation();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    order_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageId = 22; // Adjusted page ID for ticket creation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.subject.trim()) {
      toast({
        title: t("Validation Error"),
        description: t("Subject is required"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: t("Validation Error"),
        description: t("Description is required"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        ...(formData.order_id && { order_id: parseInt(formData.order_id) })
      };

      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/client/ticket/create?page_id=${pageId}`,
        "POST",
        payload
      );

      if (response.status === "success") {
        toast({
          title: t("Success"),
          description: response.message || t("Ticket created successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        
        // Reset form
        setFormData({
          subject: "",
          description: "",
          order_id: "",
        });
        
        // Notify parent component
        if (onTicketCreated) {
          onTicketCreated();
        }
      } else {
        throw new Error(response.message || "Failed to create ticket");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast({
        title: t("Error"),
        description: error.message || t("Failed to create ticket"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>{t("Subject")}</FormLabel>
          <Input
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder={t("Enter ticket subject")}
            maxLength={200}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{t("Related Order ID")} ({t("Optional")})</FormLabel>
          <Input
            name="order_id"
            type="number"
            value={formData.order_id}
            onChange={handleInputChange}
            placeholder={t("Enter order ID if related")}
          />
          <Text fontSize="sm" color="gray.500" mt={1}>
            {t("Leave empty if not related to a specific order")}
          </Text>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>{t("Description")}</FormLabel>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t("Describe the issue in detail")}
            rows={5}
            maxLength={1000}
          />
          <Text fontSize="sm" color="gray.500" mt={1}>
            {formData.description.length}/1000 {t("characters")}
          </Text>
        </FormControl>

        <Button
          type="submit"
          colorScheme="cyan"
          color="white"
          isLoading={isSubmitting}
          loadingText={t("Creating...")}
          size="lg"
        >
          {t("Create Ticket")}
        </Button>
      </VStack>
    </Box>
  );
};

export default CreateTicket;
